import cloneDeep from 'lodash/cloneDeep';

import { HostListener, Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { SetPreferences } from '../preferences/preferences.actions';
import { processUserPreferencesResponse } from '../preferences/preferences-service/process-user-preferences-response';
import { GET_PREFERENCES } from '../preferences/preferences.store';
import { mapCurrentUserToPreferences, mapCurrentUserToUser } from './current-user.utils';
import { CurrentUserResponse } from './response-models/current-user-response-model';
import { TUserResponse } from './response-models/user-response-model';
import { ClearUser, SetUser, UpdateUserName, UpdateUserType } from './user.actions';
import { TUser } from './user.model';
import { SET_USER } from './user.store';

import { UserApiProviderService } from '@core/api';
import { pushUserToDataLayer } from '../../services/analytics/google-analytics';
import { IntercomService } from '../../services/intercom.service';
import { AuthService } from '../auth/auth.service';
import { ResponseErrorService } from '../errors/response-error.service';
import { WindowService } from '@core/services';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import * as Sentry from '@sentry/angular';
import { takeUntil } from 'rxjs/operators';
import { getTimeZone } from 'src/app/core/helpers/get-timezone';
import { routeToUrl } from 'src/app/core/helpers/route-to-url';
import { getUserAuth } from 'src/app/core/http/user-auth';
import {
  TUpdateUserRequest,
  TUpdateUserType,
} from '../../data-providers/api-providers/user-api-provider/user-requests.model';
import { EAuthRoute } from '../../shared/constants/auth.constants';
import { EStore } from '../../shared/enums/store.enum';
import { logout$ } from '../auth/logout.service';
import { getSentryEnabled, logErrorInSentry } from '../errors/response-error';
import { SUPPORTED_BROWSERS_PATH } from '../supported-browsers/supported-browsers.constants';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class UserService implements OnDestroy {
  private user$: Observable<TUser>;
  private user: TUser;
  private cachedCurrentUser$: Observable<TUser> | null = null;

  private readonly destroy$ = new Subject<void>();
  private window = this.windowService.getGlobalObject();

  timezoneUpdated: boolean;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private store: Store<{ user: TUser }>,
    private router: Router,
    private authService: AuthService,
    private intercomService: IntercomService,
    private userApiProviderService: UserApiProviderService,
    private responseErrorService: ResponseErrorService,
    private windowService: WindowService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.user$ = this.store.pipe(select(EStore.USER));

    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = cloneDeep(user);

      SET_USER(cloneDeep(user));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  @HostListener('window:focus', ['$event'])
  onWindowFocus(): void {
    if (
      this.router.url.includes('passwords') ||
      this.router.url.includes('verify') ||
      this.router.url.includes(EAuthRoute.SIGNUP) ||
      this.router.url.includes(SUPPORTED_BROWSERS_PATH) ||
      this.router.url.includes('reset') ||
      this.router.url.includes(EAuthRoute.LOGIN)
    ) {
      return;
    }

    const userAuth = getUserAuth();

    if (userAuth?.email && this.user?.email) {
      if (userAuth?.email !== this.user.email) {
        this.logout();
      }
    } else if (!userAuth?.email) {
      this.logout();
    }
  }

  getUser(): TUser {
    return this.user;
  }

  clearUser(): void {
    this.store.dispatch(new ClearUser());
    this.timezoneUpdated = false;
    this.clearCachedCurrentUser();
  }

  generateUser(entity: TUserResponse): TUser {
    let unreadNotifications = false;

    if (entity.notificationStatus) {
      unreadNotifications = entity.notificationStatus.unreadNotifications;
    }

    return {
      createdOn: entity.header.createdEpochMillis,
      activeWorkspaceId: entity.activeWorkspaceRef ? entity.activeWorkspaceRef.id : null,
      accountId: entity.accountRef ? entity.accountRef.id : null,
      email: entity.email,
      userName: entity.name,
      userType: entity.userType,
      userId: entity._id,
      intercomHash: entity.intercomHash,
      avatarId: entity.images.length > 0 ? entity.images[0].id : null,
      unreadNotifications,
      enabled2fa: entity.enabled2fa,
      setup2faAfter: entity.setup2faAfter,
    };
  }

  handleUnauthorizedError(error: HttpErrorResponse): Observable<never> {
    this.authService.clearAuth();
    this.responseErrorService.errors(error.status);

    if (this.router.url !== '/login') {
      logErrorInSentry({ error, message: 'Failed to fetch user' });
    }

    this.intercomService.shutdownIntercom();

    return throwError(() => error);
  }

  handleSuccessfulLogin(newUser: TUser): TUser {
    const event = this.document.createEvent('Event');

    event.initEvent('login', true, true);
    this.window.dispatchEvent(event);

    this.intercomService.startIntercom(newUser);
    this.store.dispatch(new SetUser(newUser));
    this.configureSentry(newUser);
    this.updateTimezone();

    pushUserToDataLayer({
      user_id: newUser.userId,
      user_is_department_head: undefined,
    });

    return newUser;
  }

  fetchUser(): Observable<TUser> {
    if (!this.featureFlagService.getFlag(EFeatureFlag.GET_CURRENT_USER_ENDPOINT)) {
      return this.fetchUserFromSelfEndpoint();
    }

    if (!this.cachedCurrentUser$) {
      this.cachedCurrentUser$ = this.fetchUserFromCurrentUserEndpoint().pipe(
        tap({ error: () => this.clearCachedCurrentUser() }),
        shareReplay(1),
      );
    }

    return this.cachedCurrentUser$;
  }

  private clearCachedCurrentUser(): void {
    this.cachedCurrentUser$ = null;
  }

  private fetchUserFromSelfEndpoint(): Observable<TUser> {
    return this.userApiProviderService.fetchUser().pipe(
      map((userResponse) => this.handleSuccessfulLogin(this.generateUser(userResponse))),
      catchError((error) => this.handleUnauthorizedError(error)),
    );
  }

  private fetchUserFromCurrentUserEndpoint(): Observable<TUser> {
    return this.userApiProviderService.fetchCurrentUser().pipe(
      tap((currentUser) => this.setPreferencesFromCurrentUser(currentUser)),
      map((currentUser) => this.handleSuccessfulLogin(mapCurrentUserToUser(currentUser))),
      catchError((error) => this.handleUnauthorizedError(error)),
    );
  }

  private setPreferencesFromCurrentUser(currentUser: CurrentUserResponse): void {
    const preferences = processUserPreferencesResponse(
      mapCurrentUserToPreferences(currentUser),
      GET_PREFERENCES(),
    );

    this.store.dispatch(new SetPreferences(preferences));
  }

  updateUserSimplified(userId: string, user: TUpdateUserRequest): Observable<TUserResponse> {
    return this.userApiProviderService
      .updateUserSimplified(userId, user)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to update user (simplified)'),
        ),
      );
  }

  updateUserName(userId: string, userName: string): Observable<TUserResponse> {
    if (!navigator.onLine) {
      return throwError(null);
    }

    const body: TUpdateUserRequest = {
      name: userName,
    };

    return this.updateUserSimplified(userId, body).pipe(
      tap((response) => {
        this.store.dispatch(
          new UpdateUserName({
            userName,
          }),
        );
      }),
    );
  }

  deleteUserAvatar(userId: string): Observable<TUserResponse> {
    return this.userApiProviderService
      .deleteUserAvatar(userId)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to delete user avatar'),
        ),
      );
  }

  updateUserType(userId: string, userType: string): Observable<TUserResponse> {
    const body: TUpdateUserType = {
      userType,
    };

    return this.userApiProviderService.updateUserType(userId, body).pipe(
      tap((response) => {
        this.store.dispatch(
          new UpdateUserType({
            userType,
          }),
        );
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to update user type'),
      ),
    );
  }

  private logout(): void {
    logout$.next();

    this.router.navigate([routeToUrl(EAuthRoute.LOGIN)]);
  }

  private configureSentry(newUser: TUser): void {
    if (getSentryEnabled()) {
      Sentry.getCurrentScope()?.setUser({
        id: newUser.userId,
        username: newUser.userName,
        email: newUser.email,
      });
    }
  }

  private updateTimezone(): void {
    if (this.timezoneUpdated) {
      return;
    }

    this.timezoneUpdated = true;
    const timezoneOffset = getTimeZone();

    this.userApiProviderService
      .updateTimezone(timezoneOffset)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to update timezone'),
        ),
      )
      .subscribe();
  }
}
